import {
  BTC_UNIT_LABEL,
  BTC_UNIT_SHIFT,
  type BtcDisplayUnit,
  toUnitFractionDigits,
} from "./btcUnit";
import { type CurrencyType, getFractionDigits } from "./getFractionDigits";

const DEFAULT_MINIMUM_FRACTION_DIGITS = 2;

/* The smallest amount ever revealed, in the currency's own unit: a satoshi for BTC. */
const SMALL_AMOUNT_FRACTION_DIGITS = 8;

type FormatCurrencyAmountArgs = {
  amount: number;
  /* Platform currency code, e.g. BTC, USDT, JPY. */
  currency: string;
  /* From the cashier-admin currency config. Missing values use the fallbacks. */
  type?: CurrencyType | null;
  maximumFractionDigits?: number | null;
  /* Defaults to 2 and never exceeds the maximum. */
  minimumFractionDigits?: number;
  /* BTC only, defaults to whole BTC. */
  unit?: BtcDisplayUnit;
  locale?: string;
  /* Shows the real digits of an amount that would otherwise read as zero. */
  revealSmallAmounts?: boolean;
};

type FormatCurrencyAmountResult = {
  /* The number alone, e.g. "72.29". */
  value: string;
  /* The unit label, e.g. "mBTC". */
  code: string;
  /* Both, e.g. "72.29 mBTC". */
  text: string;
};

/*
 * Moves the decimal point through the number's decimal text, so 0.07229 in
 * mBTC is exactly 72.29 and not 72.28999999999999 as with a multiplication.
 */
const shiftDecimalPoint = (amount: number, places: number) => {
  if (places === 0) {
    return amount;
  }
  const [mantissa, exponent = "0"] = `${amount}`.split("e");
  return Number(`${mantissa}e${Number(exponent) + places}`);
};

const formatNumber = (
  value: number,
  locale: string,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
    roundingMode: "trunc",
  }).format(value);

/*
 * Formats an amount the way the cashier shows it: the admin precision for the
 * currency (or the fallbacks), the BTC unit shift, truncation (never rounding
 * up), at least two decimals where the precision allows, and the real digits
 * of amounts too small for that precision.
 */
const formatCurrencyAmount = ({
  amount,
  currency,
  type,
  maximumFractionDigits,
  minimumFractionDigits = DEFAULT_MINIMUM_FRACTION_DIGITS,
  unit = "BTC",
  locale = "en",
  revealSmallAmounts = true,
}: FormatCurrencyAmountArgs): FormatCurrencyAmountResult => {
  const isBtc = currency === "BTC";
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const displayAmount = isBtc
    ? shiftDecimalPoint(safeAmount, BTC_UNIT_SHIFT[unit])
    : safeAmount;
  const precision = getFractionDigits(currency, {
    type,
    fractionDigits: maximumFractionDigits,
  });
  const maximum = isBtc ? toUnitFractionDigits(precision, unit) : precision;
  const minimum = Math.min(minimumFractionDigits, maximum);
  const code = isBtc ? BTC_UNIT_LABEL[unit] : currency;

  const isHiddenByPrecision =
    safeAmount !== 0 && Math.abs(displayAmount) < 10 ** -maximum;
  const revealMaximum = isBtc
    ? toUnitFractionDigits(SMALL_AMOUNT_FRACTION_DIGITS, unit)
    : SMALL_AMOUNT_FRACTION_DIGITS;
  const value = formatNumber(
    displayAmount,
    locale,
    minimum,
    revealSmallAmounts && isHiddenByPrecision
      ? Math.max(revealMaximum, maximum)
      : maximum,
  );

  return { value, code, text: `${value} ${code}` };
};

export type { FormatCurrencyAmountArgs, FormatCurrencyAmountResult };
export { formatCurrencyAmount };
